import useProcessNewFile from 'common/Hooks/useProcessNewFile'
import { UNIT } from 'common/Utils/processNewFileUtils/measureUtils'
import Loader from 'components/Common/Loader'
import Modal from 'components/Common/Modal/Modal'
import PartCard from 'components/Common/PartCard/PartCard'
import StlViewer from 'components/Common/StlViewer/StlViewer'
import { FileUploadButton } from 'components/FileUploadButton'
import { ModalMessage } from 'pages/UserProfile/ModalMessage'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { deleteOldPartFiles, updateParts } from 'store/actions'

export const ACCEPTED_3D_FORMATS = [
	'step',
	'stp',
	'sldprt',
	'ipt',
	'prt',
	'sat',
	'catpart',
	'x_t',
	'x_b',
	'3dm',
	'stl',
	'obj',
]

/**
 * Allow user to upload a new 3D file linked to a Part, preview it and its CAD data, and modify Unit if it's an STL.
 * Allow Cancel (nothing is saved) or Accept (files are uploaded, Part CAD data is updated)
 * TODO (later): PGO-1310 - Subir varias piezas a la vez
 */
const Update3DModelModal = (props) => {

	const {
		// props given by parent:
		isOpen,
		closeModal,
		selectedRows,
		allParts,

		// props from withTranslation:
		t,

		// props from connect mapStateToProps:
		deleteOldPartFilesState,
		updatePartsState,
		partConfigOptions,

		// props from connect mapDispatchToProps:
		deleteOldPartFiles,
		updateParts,

	} = props

	// logic:
	const rowSelected = selectedRows?.[0]
	const partId = rowSelected?.id.value
	const [status, setStatus] = useState(/** @type {string | undefined} */(undefined))

	// files:
	const [fileSelected, setFileSelected] = useState(/** @type {File | undefined} */(undefined))

	// data shown in the front end:
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
	const [errorMessageKey, setErrorMessageKey] = useState(/** @type {string} */('error'))

	const { start, reset, accept, setUnit, processStatuses, result } = useProcessNewFile()

	const hookStatus = partId ? processStatuses[partId] : undefined
	const hookIsBusy = hookStatus != null && !['done', 'error', 'preview'].includes(hookStatus)
	const isLoading = hookIsBusy || ['updating_database', 'deleting_old_files'].includes(status)
	const displayStatus = hookIsBusy ? hookStatus : status

	// Unit selected by the user when previewing an STL file:
	const [selectedUnit, setSelectedUnit] = useState(/** @type {string} */(UNIT.millimeters))

	// Object URL for the STL preview — created when we reach 'preview', revoked after
	const [stlObjectUrl, setStlObjectUrl] = useState(/** @type {string | undefined} */(undefined))

	useEffect(() => {
		if (hookStatus !== 'preview' || !partId) return
		const stlFile = result[partId]?.stlFile
		if (!stlFile) return
		const url = URL.createObjectURL(stlFile)
		setStlObjectUrl(url)
		return () => {
			URL.revokeObjectURL(url)
			setStlObjectUrl(undefined)
		}
		// Intentionally omitting `result` — stlFile never changes while in 'preview',
		// and including it would cause the URL to be revoked/recreated on every setUnit() call.
	}, [hookStatus, partId])

	const calculateWeight_g = (cadData) => {
		if (cadData?.volume_cm3 == null) return
		const part = allParts.find(part => part.id == partId)
		const materialDensity_g_per_cm3 = partConfigOptions.fields.material.options[part.config.material].density
		return cadData.volume_cm3 * materialDensity_g_per_cm3
	}

	// When useProcessNewFile finishes, update the database:
	useEffect(() => {
		if (!partId) return
		const processResult = result[partId]
		if (!processResult?.finishedProcessing) return

		if (processResult.cadError) {
			setStatus('error')
			setErrorMessageKey(processResult.cadError === 'unknown_error' ? 'error' : processResult.cadError)
			setIsErrorModalOpen(true)
			return
		}

		setStatus('updating_database')
		updateParts({
			parts: [{
				id: partId,
				name: fileSelected.name,
				size: processResult.cadData.size_mm,
				volume: processResult.cadData.volume_cm3,
				area: processResult.cadData.area_cm2,
				faces: processResult.cadData.faces,
				process: processResult.cadData.process,
				boundaryBoxVolume: processResult.cadData.boundaryBoxVolume_cm3,
				chipVolume: processResult.cadData.chipVolume_cm3,
				weight: calculateWeight_g(processResult.cadData),
				...processResult.onShapeIds,
			}],
		}, 'upload new version')
	}, [result, partId])

	useEffect(() => {
		if (deleteOldPartFilesState?.status === 'finished') {
			if (deleteOldPartFilesState.error) {
				setStatus('error')
				setIsErrorModalOpen(true)
				return
			}
			setStatus('success')
			setIsSuccessModalOpen(true)
		}
	}, [deleteOldPartFilesState])

	useEffect(() => {
		if (status !== 'updating_database') return
		if (updatePartsState?.status === 'finished') {
			if (updatePartsState.error) {
				setStatus('error')
				setIsErrorModalOpen(true)
				return
			}
			setStatus('deleting_old_files')
			deleteOldPartFiles({
				body: {
					data: {
						partId,
					},
				},
			})
		}
	}, [updatePartsState, status])

	const handleSelectFile = (file) => {
		setFileSelected(file)
		start({
			originalFile: file,
			partId,
		})
	}

	return useMemo(() => (
		<>
			<Modal
				className="update-3d-model-modal"
				isOpen={isOpen}
				closeModal={closeModal}
				size={hookStatus === 'preview' ? 'xl' : undefined}
				onClose={() => {
					if (partId) reset(partId) // <-- ?? will not cause problems? TODO
					setStatus(undefined)
					setFileSelected(undefined)
					setIsErrorModalOpen(false)
				}}
				title={t('upload_new_version', { ns: 'naming' })}
				disableInteractions={isLoading || status === 'continue'}
				dontCloseOnBackgroundClick={isLoading || status === 'continue'}
				body={
					<div className="m-2">
						{selectedRows.length == 1 ? (
							<div className="d-flex justify-content-center">
								<div
									key={rowSelected.id.value}
									className='d-flex flex-column justify-content-center align-items-center gap-4'
								>
									{/* Information about the part: */}
									<PartCard
										img={rowSelected.fileLinks.thumbnail}
										stl={rowSelected.fileLinks.stlModel}
										geometryData={{
											partId: rowSelected.id.value,
											partName: rowSelected.name,
											size: rowSelected.size,
											weight: rowSelected.weight,
										}}
										partId={rowSelected.id.value}
										fileName={rowSelected.name}
										materialColorFinish={(
											<>
												{rowSelected.material}
												{rowSelected.finishing}
											</>
										)}
										technology={rowSelected.technology}
										size={rowSelected.size}
									/>

									{/* Preview step: show STL result and let user accept or cancel */}
									{hookStatus === 'preview' && stlObjectUrl && (() => {
										const previewResult = result[partId]
										const cadData = previewResult?.cadData
										const cadDataRounded = cadData == null ? {} : {
											size_mm: {
												x: cadData.size_mm.x.toFixed(2),
												y: cadData.size_mm.y.toFixed(2),
												z: cadData.size_mm.z.toFixed(2),
											},
											volume_cm3: cadData.volume_cm3.toFixed(2),
											area_cm2: cadData.area_cm2.toFixed(2),
											boundaryBoxVolume_cm3: cadData.boundaryBoxVolume_cm3.toFixed(2),
											chipVolume_cm3: cadData.chipVolume_cm3.toFixed(2),
										}
										const isStl = fileSelected?.name?.split('.').pop()?.toLowerCase() === 'stl'
										return (
											<div className="d-flex flex-column align-items-center gap-3" style={{ width: '100%' }}>
												<StlViewer
													key={selectedUnit}
													extension=".stl"
													modelSrc={stlObjectUrl}
													partName={fileSelected?.name ?? ''}
													partId={partId}
													size={cadData?.size_mm ? { ...cadData.size_mm, measure: 'mm' } : undefined}
													weight={cadData ? { value: calculateWeight_g(cadData), measure: 'g' } : undefined}
													stepAnalysis={previewResult?.stepAnalysis}
													windowSize={{ width: 800, height: 500 }}
													hideData={false}
												/>

												{/* Unit selector — only for STL (raw unitless data) */}
												{isStl && (
													<div className="d-flex align-items-center gap-2">
														<strong>Unit:</strong>
														{[UNIT.millimeters, UNIT.centimeters, UNIT.inches].map(u => (
															<label key={u} className="d-flex align-items-center gap-1 mb-0" style={{ cursor: 'pointer' }}>
																<input
																	type="radio"
																	name="stl-unit"
																	value={u}
																	checked={selectedUnit === u}
																	onChange={() => { setSelectedUnit(u); setUnit(partId, u) }}
																/>
																{u}
															</label>
														))}
													</div>
												)}

												{/* Properties (debug) */}
												{cadData && (
													<table className="table table-sm table-bordered" style={{ width: 'auto', fontSize: '0.85rem' }}>
														<tbody>
															<tr><td>Bounding box (mm)</td><td>{cadDataRounded.size_mm ? `${cadDataRounded.size_mm.x} × ${cadDataRounded.size_mm.y} × ${cadDataRounded.size_mm.z}` : '-'}</td></tr>
															<tr><td>Volume (cm³)</td><td>{cadDataRounded.volume_cm3 ?? '-'}</td></tr>
															<tr><td>Area (cm²)</td><td>{cadDataRounded.area_cm2 ?? '-'}</td></tr>
															<tr><td>Bounding box vol. (cm³)</td><td>{cadDataRounded.boundaryBoxVolume_cm3 ?? '-'}</td></tr>
															<tr><td>Chip volume (cm³)</td><td>{cadDataRounded.chipVolume_cm3 ?? '-'}</td></tr>
														</tbody>
													</table>
												)}

												{/* Manufacturing warnings — non-blocking, user can still accept */}
												{previewResult?.stepAnalysis?.manufacturingErrors?.length > 0 && (
													<div className="alert alert-warning mb-0 p-2" style={{ fontSize: '0.85rem' }}>
														<ul className="mb-0 ps-3">
															{previewResult.stepAnalysis.manufacturingErrors.map(errorKey => (
																<li key={errorKey}>{t(errorKey, { ns: 'naming' })}</li>
															))}
														</ul>
													</div>
												)}

												<div className="d-flex gap-2">
													<button
														className="btn btn-primary"
														onClick={() => accept(partId)}
													>
														{t('accept', { ns: 'naming' })}
													</button>
													<button
														className="btn btn-secondary"
														onClick={() => {
															reset(partId)
															setFileSelected(undefined)
															setSelectedUnit('millimeters')
														}}
													>
														{t('cancel', { ns: 'naming' })}
													</button>
												</div>
											</div>
										)
									})()
									}

									{/* Button to upload new 3D model: */}
									{hookStatus !== 'preview' && (
										<FileUploadButton
											additionalButtonProps={{
												disabled: isLoading,
												style: {
													width: '100%',
													cursor: isLoading ? 'wait' : 'pointer',
													background: isLoading ? 'gray' : '#007bff',
													color: 'white',
													padding: '8px 16px',
													borderRadius: '4px',
													border: 'none',
												},
												children: (
													isLoading ? (
														<div className='d-flex justify-content-center align-items-center gap-1'>
															<Loader />
															{t(displayStatus, { ns: 'naming' })}
														</div>
													) : (
														t('upload_new_version', { ns: 'naming' })
													)
												),
											}}
											additionalInputProps={{
												disabled: isLoading,
												accept: ACCEPTED_3D_FORMATS.map(x => `.${x}`).join(','),
												multiple: false,
												onChange: (e) => {
													if (e.target.files && e.target.files[0]) {
														const file = e.target.files[0]
														handleSelectFile(file)
													}
													if (e.currentTarget) e.currentTarget.value = null
												},
											}}
										/>
									)}
								</div>
							</div>
						) : (
							// You can only update the 3D model of 1 part at a time:
							<div className="modal-body">
								{t('select_only_one_part', { ns: 'naming' })}
							</div>
						)}
					</div>
				}
			/>
			<ModalMessage
				id="error-modal"
				isModalOpen={isErrorModalOpen}
				closeModal={() => {
					setIsErrorModalOpen(false)
				}}
				message={t(errorMessageKey, { ns: 'naming' })}
			/>
			<ModalMessage
				id="success-modal"
				isModalOpen={isSuccessModalOpen}
				closeModal={() => {
					history.go(0)
				}}
				message={t('update_success', { ns: 'naming' })}
			/>
		</>

	), [
		isOpen,
		t,
		status,
		hookStatus,
		stlObjectUrl,
		selectedUnit,
		result,
		partId,
		isErrorModalOpen,
		errorMessageKey,
	])
}

Update3DModelModal.propTypes = {

	// props given by parent:
	isOpen: PropTypes.bool,
	closeModal: PropTypes.func,
	selectedRows: PropTypes.array,
	allParts: PropTypes.array,

	// props from withTranslation:
	t: PropTypes.func,

	// props from connect mapStateToProps:
	deleteOldPartFilesState: PropTypes.object,
	updatePartsState: PropTypes.object,
	partConfigOptions: PropTypes.object,

	// props from connect mapDispatchToProps:
	deleteOldPartFiles: PropTypes.func,
	updateParts: PropTypes.func,
}

const mapStateToProps = state => ({
	deleteOldPartFilesState: state.CloudStorage.deleteOldPartFilesState,
	updatePartsState: state.Parts.updatePartsState,
	partConfigOptions: state.Parts.partConfigOptions,
})

const mapDispatchToProps = {
	deleteOldPartFiles,
	updateParts,
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Update3DModelModal))
